import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Cloze, clozeAPI, API_BASE_URL } from "../services/api";
import { useSound } from "../hooks/useSound";
import { useSettings } from "../hooks/useSettings";

interface ClozeWithResult extends Cloze {
  userAnswer?: string;
  isCorrect?: boolean;
  showResult?: boolean;
}

const ClozePractice: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { playCorrectSound, playIncorrectSound } = useSound();
  const { settings } = useSettings();

  const [clozes, setClozes] = useState<ClozeWithResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [startTime, setStartTime] = useState<number>(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSuccessAnimating, setIsSuccessAnimating] = useState(false);
  const [practiceComplete, setPracticeComplete] = useState(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);

  // Check if device is mobile/touch-enabled
  const isMobile = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) ||
      "ontouchstart" in window ||
      (window.innerWidth <= 768 && window.innerHeight <= 1024)
    );
  };

  useEffect(() => {
    if (!collectionId) return;

    const fetchClozes = async () => {
      try {
        const response = await clozeAPI.getClozes(
          parseInt(collectionId),
          getAccessTokenSilently,
          settings.exerciseOrder
        );
        setClozes(response.data.clozes);
        setStartTime(Date.now());
      } catch (err: any) {
        setError("Failed to load clozes. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchClozes();
  }, [collectionId, getAccessTokenSilently, settings.exerciseOrder]);

  const currentCloze = clozes[currentIndex];

  // Keyboard navigation (disabled on mobile)
  useEffect(() => {
    if (!currentCloze || isMobile()) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Audio playback (works anytime)
      if (e.key === "i" || e.key === "I") {
        e.preventDefault();
        speakGreekText();
        return;
      }

      // Continue after incorrect answer
      if (
        e.key === "Enter" &&
        currentCloze.showResult &&
        !currentCloze.isCorrect &&
        currentIndex < clozes.length - 1
      ) {
        e.preventDefault();
        handleContinue();
        return;
      }

      // Navigation keys only work when not showing result
      if (
        submitting ||
        currentCloze.showResult ||
        !currentCloze.choices ||
        currentCloze.choices.length === 0
      ) {
        return;
      }

      const optionsCount = currentCloze.choices!.length;

      // Navigation keys
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setFocusedOptionIndex((prev) => (prev + 1) % optionsCount);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setFocusedOptionIndex(
          (prev) => (prev - 1 + optionsCount) % optionsCount
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const focusedChoice = currentCloze.choices![focusedOptionIndex];
        if (focusedChoice) {
          handleChoiceClick(focusedChoice.word);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    currentCloze,
    submitting,
    focusedOptionIndex,
    currentIndex,
    clozes.length,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset focused option index when cloze changes
  useEffect(() => {
    setFocusedOptionIndex(0);
  }, [currentIndex]);

  const handleReturnToCollections = () => {
    navigate("/practice");
  };

  const handleSubmitAnswer = async (answer?: string) => {
    const answerToSubmit = answer || selectedAnswer;
    if (!currentCloze || !answerToSubmit) return;

    setSubmitting(true);
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);

    try {
      const response = await clozeAPI.submitAnswer(
        currentCloze.id,
        answerToSubmit,
        timeTaken,
        getAccessTokenSilently
      );

      // Update cloze with result
      const updatedClozes = [...clozes];
      updatedClozes[currentIndex] = {
        ...currentCloze,
        userAnswer: answerToSubmit,
        isCorrect: response.data.isCorrect,
        showResult: true,
      };
      setClozes(updatedClozes);
      setSelectedAnswer("");

      // Play sound effect based on result
      if (response.data.isCorrect) {
        playCorrectSound();
        // Check if this is the last question
        if (currentIndex === clozes.length - 1) {
          // Show success animation, then completion screen
          setIsSuccessAnimating(true);
          setTimeout(() => {
            setIsSuccessAnimating(false);
            setPracticeComplete(true);
          }, 750); // 1.5 seconds
        } else {
          // Show success animation before advancing to next question
          setIsSuccessAnimating(true);
          setTimeout(() => {
            setIsSuccessAnimating(false);
            setCurrentIndex(currentIndex + 1);
            setStartTime(Date.now());
            setSelectedAnswer("");
            setError("");
          }, 750); // 1.5 seconds
        }
      } else {
        playIncorrectSound();
      }
    } catch (err: any) {
      setError("Failed to submit answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChoiceClick = async (choice: string) => {
    if (submitting) return;

    setSelectedAnswer(choice);
    // Auto-submit the answer
    await handleSubmitAnswer(choice);
  };

  const handleContinue = () => {
    if (currentIndex < clozes.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setStartTime(Date.now());
      setSelectedAnswer("");
      setError("");
    }
  };

  const speakGreekText = async () => {
    if (!currentCloze || isSpeaking) return;

    setIsSpeaking(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const authHeaders = {
        Authorization: `Bearer ${accessToken}`,
      };

      // Always fetch audio data first (GET for cached, POST for new)
      let audioResponse;

      const getResponse = await fetch(
        `${API_BASE_URL}/clozes/${currentCloze.id}/audio`,
        {
          method: "GET",
          headers: authHeaders,
        }
      );

      if (getResponse.ok) {
        // Use cached audio
        audioResponse = getResponse;
      } else {
        // Generate new audio
        audioResponse = await fetch(
          `${API_BASE_URL}/clozes/${currentCloze.id}/audio`,
          {
            method: "POST",
            headers: authHeaders,
          }
        );
      }

      if (!audioResponse.ok) {
        console.error("Failed to get audio, status:", audioResponse.status);
        const errorText = await audioResponse.text();
        console.error("Error response:", errorText);
        setIsSpeaking(false);
        return;
      }

      // Convert response to blob and create audio element
      const audioBlob = await audioResponse.blob();

      if (audioBlob.size === 0) {
        console.error("Audio blob is empty!");
        setIsSpeaking(false);
        return;
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        console.log("Audio playback ended");
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };

      audio.oncanplay = () => {
        console.log("Audio can play now");
      };

      audio.oncanplaythrough = () => {
        console.log("Audio can play through without buffering");
      };

      audio.onloadstart = () => {
        console.log("Audio load started");
      };

      audio.onloadeddata = () => {
        console.log("Audio data loaded");
      };

      audio.onloadedmetadata = () => {
        console.log("Audio metadata loaded");
      };

      audio.onabort = () => {
        console.log("Audio loading aborted");
      };

      audio.onstalled = () => {
        console.log("Audio loading stalled");
      };

      audio.onerror = (e) => {
        console.error("Audio playback error:", e);
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };

      // Initialize audio context if needed for browser autoplay policies
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        try {
          const audioContext = new AudioContextClass();
          if (audioContext.state === "suspended") {
            audioContext.resume();
          }
        } catch (e) {
          console.log("Audio context initialization failed:", e);
        }
      }

      audio.volume = 0.8; // Set reasonable volume

      // Wait for audio to be ready before playing
      const tryPlayAudio = async () => {
        try {
          if (audio.readyState >= 2) {
            // HAVE_CURRENT_DATA or better
            await audio.play();
            console.log("Audio started playing successfully");
          } else {
            console.log("Audio not ready yet, waiting...");
            setTimeout(tryPlayAudio, 100);
          }
        } catch (e) {
          console.error("Audio play failed:", e);
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
        }
      };

      // Start trying to play audio
      tryPlayAudio();
    } catch (error) {
      console.error("TTS error:", error);
      setIsSpeaking(false);
      // Show user-friendly error message
      alert(
        "Audio playback failed. Please try again or check your browser's autoplay settings."
      );
    }
  };

  // Helper function to clean Greek text
  const cleanGreekText = (text: string) => {
    return text.replace(/[⸃⸀]/g, "");
  };

  const renderClozeSentence = (cloze: ClozeWithResult) => {
    const cleanedSentence = cleanGreekText(cloze.greek_sentence);
    const parts = cleanedSentence.split("___");

    // Determine what to show in the blank
    let displayWord = "___";
    if (cloze.showResult) {
      // Always show the correct word when showing results
      displayWord = cloze.correct_word;
    }

    return (
      <div className="text-xl text-teal-700 dark:text-teal-500 leading-relaxed font-medium max-w-2xl">
        {parts[0]}
        <span className={`${cloze.showResult ? "font-bold" : ""}`}>
          {displayWord}
        </span>
        {parts[1]} <span className="text-sm text-zinc-500">(SBLGNT)</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error && clozes.length === 0) {
    return (
      <div className="text-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!currentCloze) {
    return (
      <div className="text-center">
        <p className="text-zinc-600">No clozes available in this collection.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {!practiceComplete && (
        <>
          {/* Progress indicator */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-zinc-600">
                Question {currentIndex + 1} of {clozes.length}
              </span>
              <span className="text-sm text-zinc-600">
                {Math.round(((currentIndex + 1) / clozes.length) * 100)}%
                Complete
              </span>
            </div>
            <div className="w-full bg-zinc-200 rounded-full h-2">
              <div
                className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentIndex + 1) / clozes.length) * 100}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Cloze content */}
          <div
            className={`bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-8 mb-8 transition-all duration-500 ${
              isSuccessAnimating
                ? "bg-teal-50 dark:bg-teal-900/20 border-teal-300 dark:border-teal-600 shadow-teal-200 dark:shadow-teal-900/50 animate-pulse"
                : ""
            }`}
          >
            {/* Scripture reference and audio button */}
            {currentCloze.reference && (
              <div className="mb-4 relative">
                <button
                  onClick={speakGreekText}
                  disabled={isSpeaking}
                  className={`absolute left-10 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors ${
                    isSpeaking
                      ? "bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-400"
                      : "bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-600"
                  }`}
                  title="Listen to Greek text (Press 'i')"
                >
                  <svg
                    className={`w-5 h-5 ${isSpeaking ? "animate-pulse" : ""}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.414 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.414l3.969-3.816a1 1 0 011.616 0zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <p className="text-xl font-medium text-zinc-600 dark:text-zinc-100 text-center">
                  {currentCloze.reference}
                </p>
              </div>
            )}

            {/* Greek sentence */}
            <div className="text-center">
              <div className="flex flex-col items-center">
                {!currentCloze.reference && (
                  <div className="flex items-center justify-center mb-4">
                    <button
                      onClick={speakGreekText}
                      disabled={isSpeaking}
                      className={`p-2 rounded-full transition-colors ${
                        isSpeaking
                          ? "bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-400"
                          : "bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-600"
                      }`}
                      title="Listen to Greek text (Press 'i')"
                    >
                      <svg
                        className={`w-5 h-5 ${
                          isSpeaking ? "animate-pulse" : ""
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.414 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.414l3.969-3.816a1 1 0 011.616 0zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                )}
                {isSpeaking && (
                  <div className="mb-2 text-center">
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 animate-pulse">
                      AI-generated audio. May contain mistakes.
                    </p>
                  </div>
                )}
                <div className="flex justify-center">
                  {renderClozeSentence(currentCloze)}
                </div>
              </div>
            </div>

            {/* Full English translation */}
            {currentCloze.context_text && (
              <div className="mb-2 p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                <p className="text-zinc-800 dark:text-zinc-100 text-base text-center max-w-2xl mx-auto">
                  {currentCloze.context_text.replace(/<\/?i>/g, "")}{" "}
                  <span className="text-sm text-zinc-500">(BLB)</span>
                </p>
              </div>
            )}

            {/* Result feedback */}
            {currentCloze.showResult && (
              <div
                className={`mb-6 p-4 rounded-md text-center ${
                  currentCloze.isCorrect
                    ? "bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800"
                    : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500"
                }`}
              >
                <div className="flex items-center justify-center">
                  <svg
                    className={`w-5 h-5 mr-2 ${
                      currentCloze.isCorrect
                        ? "text-teal-600 dark:text-teal-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={
                        currentCloze.isCorrect
                          ? "M5 13l4 4L19 7"
                          : "M6 18L18 6M6 6l12 12"
                      }
                    />
                  </svg>
                  <span
                    className={`font-medium text-lg ${
                      currentCloze.isCorrect
                        ? "text-teal-800 dark:text-teal-400"
                        : "text-red-800 dark:text-red-500"
                    }`}
                  >
                    {currentCloze.isCorrect ? "Correct!" : "Incorrect"}
                  </span>
                </div>
                {!currentCloze.isCorrect && (
                  <p className="text-red-700 dark:text-red-500 mt-2">
                    Correct answer:{" "}
                    <strong className="greek-text">
                      {cleanGreekText(currentCloze.correct_word)}
                    </strong>
                  </p>
                )}

                {/* Continue button - only show for incorrect answers */}
                {currentIndex < clozes.length - 1 &&
                  !currentCloze.isCorrect && (
                    <div className="mt-4 flex justify-center">
                      <button
                        onClick={handleContinue}
                        className="px-6 py-2 bg-teal-600 dark:bg-slate-600 text-white rounded-md hover:bg-teal-700 dark:hover:bg-slate-500 transition-colors"
                      >
                        Continue (Press Enter)
                      </button>
                    </div>
                  )}
              </div>
            )}

            {/* Answer choices or input */}
            {!currentCloze.showResult &&
            currentCloze.choices &&
            currentCloze.choices.length > 0 ? (
              <div className="text-center">
                <div className="grid grid-cols-1 gap-3 max-w-2xl mx-auto">
                  {currentCloze.choices.map((choice, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setFocusedOptionIndex(index);
                        handleChoiceClick(choice.word);
                      }}
                      onMouseEnter={() => setFocusedOptionIndex(index)}
                      className={`p-4 border rounded-lg text-center transition-all duration-200 text-lg font-medium break-all greek-text ${
                        selectedAnswer === choice.word
                          ? "border-teal-500 dark:border-teal-400 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 ring-2 ring-teal-300 dark:ring-teal-600"
                          : !isMobile() &&
                            focusedOptionIndex === index &&
                            !submitting
                          ? "border-teal-400 dark:border-teal-500 bg-teal-50/50 dark:bg-teal-900/10 text-teal-600 dark:text-teal-400 ring-2 ring-teal-200 dark:ring-teal-700"
                          : "border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-zinc-100 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:text-teal-600 dark:hover:text-teal-400 hover:border-teal-400 dark:hover:border-teal-500"
                      }`}
                      disabled={submitting}
                    >
                      {cleanGreekText(choice.word)}
                      {submitting && selectedAnswer === choice.word && (
                        <div className="ml-2 inline-block">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              !currentCloze.showResult && (
                <div className="text-center">
                  <h4 className="text-lg font-medium text-zinc-800 dark:text-zinc-100 mb-4">
                    Enter the missing word:
                  </h4>
                  <div className="max-w-md mx-auto">
                    <input
                      type="text"
                      value={selectedAnswer}
                      onChange={(e) => setSelectedAnswer(e.target.value)}
                      className="w-full px-4 py-3 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-transparent bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 text-lg"
                      placeholder="Type your answer..."
                      disabled={submitting}
                    />
                  </div>
                </div>
              )
            )}

            {/* Error message */}
            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded text-center">
                {error}
              </div>
            )}
          </div>
        </>
      )}

      {/* Practice completion screen */}
      {practiceComplete && (
        <div className="mt-8 p-8 bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700">
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-teal-600 dark:text-teal-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
              Practice Complete!
            </h3>
            <p className="text-zinc-600 dark:text-zinc-400 mb-6">
              Great job! You've completed all the questions in this collection.
            </p>
            <div className="flex justify-center">
              <button
                onClick={handleReturnToCollections}
                className="px-6 py-3 bg-teal-600 dark:bg-teal-700 text-white rounded-md hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors"
              >
                Return to Collections
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClozePractice;
